import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle2, Crown, Loader2, Sparkles, Receipt, Calendar as CalendarIcon, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const FEATURES = [
  'Calendrier & rendez-vous illimités',
  'Devis & Factures (HT/TTC, TVA libre)',
  'Compte rendu, Charges & Bénéfice',
  'Module Prévisionnel + Météo',
];

function fmtDate(iso) {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'dd/MM/yyyy', { locale: fr }); } catch (e) { return '—'; }
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'dd/MM/yyyy HH:mm', { locale: fr }); } catch (e) { return '—'; }
}

function buildStatusInfo(data) {
  const status = data ? data.subscription_status : null;
  const plan = data ? data.subscription_plan : null;
  const trialEnd = data ? data.trial_ends_at : null;
  const subEnd = data ? data.subscription_ends_at : null;

  let label = 'Aucun abonnement';
  let color = 'slate';
  let detail = 'Choisissez un plan ci-dessous pour commencer.';
  let endDate = null;
  let daysLeft = null;
  let endLabel = '';

  if (status === 'UNLIMITED') {
    label = 'Accès illimité gratuit';
    color = 'amber';
    detail = "Offert par l'administrateur. Aucun paiement nécessaire.";
  } else if (status === 'TRIAL' && trialEnd) {
    label = 'Essai gratuit en cours';
    color = 'sky';
    endDate = trialEnd;
    endLabel = "Fin de l'essai";
    daysLeft = Math.max(0, Math.ceil((new Date(trialEnd) - new Date()) / 86400000));
    detail = `Votre essai gratuit se termine dans ${daysLeft} jour${daysLeft > 1 ? 's' : ''}.`;
  } else if (status === 'ACTIVE' && subEnd) {
    label = `Abonnement ${plan === 'yearly' ? 'annuel' : 'mensuel'} actif`;
    color = 'emerald';
    endDate = subEnd;
    endLabel = 'Échéance';
    daysLeft = Math.max(0, Math.ceil((new Date(subEnd) - new Date()) / 86400000));
    detail = `Renouvellement nécessaire dans ${daysLeft} jour${daysLeft > 1 ? 's' : ''}.`;
  } else if (status === 'EXPIRED') {
    label = 'Abonnement expiré';
    color = 'red';
    detail = "Choisissez un plan pour réactiver l'accès.";
  }

  return { label, color, detail, endDate, daysLeft, endLabel };
}

const COLOR_MAP = {
  slate: 'bg-slate-50 border-slate-200 text-slate-900',
  amber: 'bg-amber-50 border-amber-200 text-amber-900',
  sky: 'bg-sky-50 border-sky-200 text-sky-900',
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  red: 'bg-red-50 border-red-200 text-red-900',
};

export const MySubscriptionPage = () => {
  const { api, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const me = await api.get('/subscription/me');
      const p = await api.get('/subscription/plans');
      setData(me.data);
      setPlans(p.data.plans || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubscribe = async (planId) => {
    setRedirecting(planId);
    try {
      const r = await api.post('/subscription/checkout', {
        plan: planId,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error('Erreur lors du paiement');
      setRedirecting(null);
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center" data-testid="my-subscription-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
      </div>
    );
  }

  const status = data ? data.subscription_status : null;
  const isUnlimited = status === 'UNLIMITED';
  const isActive = status === 'ACTIVE';
  const info = buildStatusInfo(data);
  const history = data && data.payment_history ? data.payment_history : [];

  return (
    <div data-testid="my-subscription-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-2xl md:text-3xl font-bold text-slate-900">Mon abonnement</h1>
          <p className="text-slate-500 text-sm md:text-base">Gérez votre abonnement et consultez votre historique de paiement.</p>
        </div>
        <Button
          variant="outline"
          onClick={() => { load(); refreshUser(); }}
          data-testid="my-sub-refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
        </Button>
      </div>

      <Card className={`border-2 mb-8 ${COLOR_MAP[info.color]}`} data-testid="my-sub-status-card">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-wide font-medium opacity-70">Statut actuel</p>
              <h2 className="font-outfit text-2xl font-bold mt-1">{info.label}</h2>
              <p className="text-sm mt-2 opacity-80">{info.detail}</p>
            </div>
            {info.endDate ? (
              <div className="text-right">
                <p className="text-xs uppercase tracking-wide font-medium opacity-70">{info.endLabel}</p>
                <p className="font-outfit text-2xl font-bold mt-1 font-mono">{fmtDate(info.endDate)}</p>
                <p className="text-sm font-mono mt-1">
                  {info.daysLeft} jour{info.daysLeft > 1 ? 's' : ''} restant{info.daysLeft > 1 ? 's' : ''}
                </p>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {!isUnlimited ? (
        <div>
          <h3 className="font-outfit text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#065F46]" />
            {isActive ? 'Renouveler ou changer de plan' : 'Choisir un abonnement'}
          </h3>
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {plans.map((p) => {
              const isYearly = p.id === 'yearly';
              const monthly = isYearly ? p.amount / 12 : p.amount;
              const buttonLabel = isActive
                ? `Renouveler — ${p.amount.toFixed(2).replace('.', ',')} €`
                : `S'abonner — ${p.amount.toFixed(2).replace('.', ',')} €`;
              return (
                <Card
                  key={p.id}
                  className={isYearly ? 'relative border-2 border-[#065F46] shadow-md' : 'relative border border-slate-200'}
                  data-testid={`my-plan-${p.id}`}
                >
                  {isYearly ? (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#065F46] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                      <Crown className="w-3 h-3" /> ÉCONOMIQUE
                    </div>
                  ) : null}
                  <CardHeader>
                    <CardTitle className="font-outfit">{p.label}</CardTitle>
                    <div className="mt-2">
                      <span className="font-outfit text-3xl font-bold text-slate-900 font-mono">
                        {p.amount.toFixed(2).replace('.', ',')} €
                      </span>
                      <span className="text-slate-500 ml-2 text-sm">
                        /{p.id === 'monthly' ? 'mois' : 'an'}
                      </span>
                      {isYearly ? (
                        <p className="text-xs text-emerald-700 mt-1 font-medium">
                          soit {monthly.toFixed(2).replace('.', ',')} €/mois
                        </p>
                      ) : null}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-1.5 mb-5">
                      {FEATURES.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      className={isYearly ? 'w-full bg-[#065F46] hover:bg-[#047857] text-white' : 'w-full bg-slate-900 hover:bg-slate-800 text-white'}
                      onClick={() => handleSubscribe(p.id)}
                      disabled={redirecting !== null}
                      data-testid={`my-subscribe-${p.id}-btn`}
                    >
                      {redirecting === p.id ? (
                        <span className="flex items-center"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirection vers Stripe…</span>
                      ) : (
                        <span>{buttonLabel}</span>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 mb-8">
            🔒 Paiement sécurisé via Stripe. Aucune donnée bancaire stockée sur nos serveurs.
          </p>
        </div>
      ) : null}

      <Card data-testid="my-sub-history-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-outfit text-lg flex items-center gap-2">
            <Receipt className="w-5 h-5 text-slate-500" /> Historique des paiements
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-center py-8 text-slate-500 text-sm">Aucun paiement pour l'instant.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500 text-xs uppercase">
                    <th className="text-left py-3 px-2 font-medium">Date</th>
                    <th className="text-left py-3 px-2 font-medium">Plan</th>
                    <th className="text-right py-3 px-2 font-medium">Montant</th>
                    <th className="text-left py-3 px-2 font-medium">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((t) => (
                    <tr key={t.session_id || t.id} className="border-b border-slate-100" data-testid={`payment-row-${t.session_id || t.id}`}>
                      <td className="py-3 px-2 text-slate-700 font-mono text-xs">
                        <CalendarIcon className="w-3 h-3 inline mr-1 text-slate-400" />
                        {fmtDateTime(t.created_at)}
                      </td>
                      <td className="py-3 px-2 capitalize">{t.plan === 'yearly' ? 'Annuel' : 'Mensuel'}</td>
                      <td className="py-3 px-2 text-right font-mono">
                        {(t.amount || 0).toFixed(2).replace('.', ',')} {(t.currency || 'eur').toUpperCase()}
                      </td>
                      <td className="py-3 px-2">
                        {t.payment_status === 'paid' ? (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">Payé</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">En attente</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MySubscriptionPage;
